import type { FolderResourceCollection, FolderResourceType } from '@/services';
import type { Folder } from '@/types';

export const FOLDER_RESOURCE_TYPES = [
    'dashboards',
    'investments',
    'lists',
    'memos',
    'pipelines',
] as const satisfies readonly FolderResourceType[];

export type FolderResource = Folder[FolderResourceType][number];

export interface FolderResourceRemoval {
    itemTitle?: string;
    resourceId: string;
    resourceType: FolderResourceType;
}

export interface RemovedFolderResourceSnapshot extends FolderResourceRemoval {
    index: number;
    item: FolderResource;
}

const resourceId = (resource: unknown): string | undefined => {
    if (!resource || typeof resource !== 'object' || !('id' in resource)) {
        return undefined;
    }

    const id = (resource as { id?: unknown }).id;

    return typeof id === 'string' || typeof id === 'number' ? String(id) : undefined;
};

const definedResourceIds = (resources: FolderResource[]): string[] =>
    resources.flatMap((resource) => {
        const id = resourceId(resource);

        return id ? [id] : [];
    });

const collectionFor = (folder: Folder, resourceType: FolderResourceType): FolderResource[] =>
    (folder[resourceType] ?? []) as FolderResource[];

const withCollection = (folder: Folder, resourceType: FolderResourceType, resources: FolderResource[]): Folder =>
    ({ ...folder, [resourceType]: resources }) as Folder;

export const normalizeFolderResourceType = (value: string): FolderResourceType | undefined => {
    const normalized = value.toLocaleLowerCase();

    return FOLDER_RESOURCE_TYPES.find((resourceType) => resourceType === normalized);
};

export const addFolderResources = (folder: Folder, resources: FolderResourceCollection): Folder =>
    FOLDER_RESOURCE_TYPES.reduce((nextFolder, resourceType) => {
        const additions = resources[resourceType] as FolderResource[] | undefined;

        if (!additions?.length) {
            return nextFolder;
        }

        const nextCollection = [...collectionFor(nextFolder, resourceType)];
        const indexById = new Map(
            nextCollection.flatMap((resource, index) => {
                const id = resourceId(resource);
                return id ? [[id, index] as const] : [];
            }),
        );

        additions.forEach((resource) => {
            const id = resourceId(resource);

            if (!id) {
                return;
            }

            const existingIndex = indexById.get(id);

            if (existingIndex === undefined) {
                indexById.set(id, nextCollection.length);
                nextCollection.push(resource);
            } else {
                nextCollection[existingIndex] = resource;
            }
        });

        return withCollection(nextFolder, resourceType, nextCollection);
    }, folder);

export const captureAddedFolderResourceIds = (
    folder: Folder,
    resources: FolderResourceCollection,
): FolderResourceRemoval[] =>
    FOLDER_RESOURCE_TYPES.flatMap((resourceType) => {
        const existingIds = new Set(definedResourceIds(collectionFor(folder, resourceType)));
        const additions = resources[resourceType] as FolderResource[] | undefined;

        return (additions ?? []).flatMap((resource) => {
            const id = resourceId(resource);

            return id && !existingIds.has(id) ? [{ resourceId: id, resourceType }] : [];
        });
    });

export const captureRemovedFolderResources = (
    folder: Folder,
    removals: FolderResourceRemoval[],
): RemovedFolderResourceSnapshot[] =>
    removals.flatMap((removal) => {
        const collection = collectionFor(folder, removal.resourceType);
        const index = collection.findIndex((resource) => resourceId(resource) === removal.resourceId);

        return index === -1 ? [] : [{ ...removal, index, item: collection[index] }];
    });

export const removeFolderResources = (folder: Folder, removals: FolderResourceRemoval[]): Folder => {
    const idsByType = removals.reduce<Map<FolderResourceType, Set<string>>>((ids, removal) => {
        const resourceIds = ids.get(removal.resourceType) ?? new Set<string>();
        resourceIds.add(removal.resourceId);
        ids.set(removal.resourceType, resourceIds);
        return ids;
    }, new Map());

    return FOLDER_RESOURCE_TYPES.reduce((nextFolder, resourceType) => {
        const resourceIds = idsByType.get(resourceType);

        if (!resourceIds?.size) {
            return nextFolder;
        }

        return withCollection(
            nextFolder,
            resourceType,
            collectionFor(nextFolder, resourceType).filter((resource) => {
                const id = resourceId(resource);
                return !id || !resourceIds.has(id);
            }),
        );
    }, folder);
};

export const restoreFolderResources = (folder: Folder, snapshots: RemovedFolderResourceSnapshot[]): Folder =>
    FOLDER_RESOURCE_TYPES.reduce((nextFolder, resourceType) => {
        const collectionSnapshots = snapshots
            .filter((snapshot) => snapshot.resourceType === resourceType)
            .sort((left, right) => left.index - right.index);

        if (!collectionSnapshots.length) {
            return nextFolder;
        }

        const nextCollection = [...collectionFor(nextFolder, resourceType)];

        collectionSnapshots.forEach((snapshot) => {
            if (nextCollection.some((resource) => resourceId(resource) === snapshot.resourceId)) {
                return;
            }

            nextCollection.splice(Math.min(snapshot.index, nextCollection.length), 0, snapshot.item);
        });

        return withCollection(nextFolder, resourceType, nextCollection);
    }, folder);

export const selectFolderResourceResponse = (
    folder: Folder,
    resources: FolderResourceCollection,
): FolderResourceCollection =>
    FOLDER_RESOURCE_TYPES.reduce<FolderResourceCollection>((selected, resourceType) => {
        const requestedIds = new Set(
            definedResourceIds((resources[resourceType] as FolderResource[] | undefined) ?? []),
        );

        if (requestedIds.size) {
            const responseResources = collectionFor(folder, resourceType).filter((resource) => {
                const id = resourceId(resource);
                return Boolean(id && requestedIds.has(id));
            });

            Object.assign(selected, { [resourceType]: responseResources });
        }

        return selected;
    }, {});
